/**
 * 글로벌 테이블 세션 스테이터스 레이어 문구 (#660).
 *
 * 활성 테이블 세션 중 앱 전역(비테이블 라우트)에서 하단 네비 위에 고정 노출되는
 * 복귀 레이어(GlobalTableSessionBar)의 라벨. 탭하면 테이블 오더 페이지로 복귀한다.
 *
 * 15개 지원 locale 전수 정의 — i18n drift 는 globalSessionBar.test.ts 가 회귀 게이트.
 * (#650 paymentResult3ds / #657 paymentResultNav 패턴 정합 — 별도 파일 + 전수 테스트)
 *
 * @see src/components/session/GlobalTableSessionBar.tsx
 */
import type { Locale } from '@/config/locales';

export interface GlobalSessionBarTranslation {
  /** 테이블 라벨 — {table} = 테이블 번호. 예: "테이블 {table}" */
  tableLabel: string;
  /** 미결제 요약 — {count} = 미결제 주문 수. 예: "미결제 {count}건" */
  unpaidSummary: string;
  /** 복귀 동작 라벨 (aria-label) — 예: "테이블 오더로 돌아가기" */
  returnLabel: string;
}

export const globalSessionBarTranslations: Record<Locale, GlobalSessionBarTranslation> = {
  es: {
    tableLabel: 'Mesa {table}',
    unpaidSummary: '{count} por pagar',
    returnLabel: 'Volver al pedido de mesa',
  },
  en: {
    tableLabel: 'Table {table}',
    unpaidSummary: '{count} unpaid',
    returnLabel: 'Back to table order',
  },
  ko: {
    tableLabel: '테이블 {table}',
    unpaidSummary: '미결제 {count}건',
    returnLabel: '테이블 오더로 돌아가기',
  },
  pt: {
    tableLabel: 'Mesa {table}',
    unpaidSummary: '{count} por pagar',
    returnLabel: 'Voltar ao pedido da mesa',
  },
  fr: {
    tableLabel: 'Table {table}',
    unpaidSummary: '{count} à payer',
    returnLabel: 'Retour à la commande de table',
  },
  zh: {
    tableLabel: '餐桌 {table}',
    unpaidSummary: '未支付 {count} 笔',
    returnLabel: '返回餐桌点单',
  },
  ja: {
    tableLabel: 'テーブル {table}',
    unpaidSummary: '未払い {count}件',
    returnLabel: 'テーブル注文に戻る',
  },
  hi: {
    tableLabel: 'टेबल {table}',
    unpaidSummary: '{count} बकाया',
    returnLabel: 'टेबल ऑर्डर पर वापस जाएं',
  },
  de: {
    tableLabel: 'Tisch {table}',
    unpaidSummary: '{count} offen',
    returnLabel: 'Zurück zur Tischbestellung',
  },
  it: {
    tableLabel: 'Tavolo {table}',
    unpaidSummary: '{count} da pagare',
    returnLabel: "Torna all'ordine al tavolo",
  },
  ru: {
    tableLabel: 'Стол {table}',
    unpaidSummary: 'Не оплачено: {count}',
    returnLabel: 'Вернуться к заказу за столом',
  },
  ar: {
    tableLabel: 'طاولة {table}',
    unpaidSummary: '{count} غير مدفوع',
    returnLabel: 'العودة إلى طلب الطاولة',
  },
  vi: {
    tableLabel: 'Bàn {table}',
    unpaidSummary: '{count} chưa thanh toán',
    returnLabel: 'Quay lại đơn gọi món tại bàn',
  },
  th: {
    tableLabel: 'โต๊ะ {table}',
    unpaidSummary: 'ค้างชำระ {count} รายการ',
    returnLabel: 'กลับไปที่ออเดอร์โต๊ะ',
  },
  nl: {
    tableLabel: 'Tafel {table}',
    unpaidSummary: '{count} openstaand',
    returnLabel: 'Terug naar tafelbestelling',
  },
};
